import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { expandTdDivsAnimation, fadeInAnimation, onlyEnter } from '@zero/shared/ui-animations';
import { PushQuoteService } from '@zero/shared/ui-push';
import { SortHelper, SorterLabelComponent, TradingSessionComponent } from '@zero/shared/ui-web-components';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { OrderData, OrderDataHolder, OrderStatus, PushMessageType, Quote, TSLUpdate } from '@zero/shared/util-models';
import { TradeService } from '@zero/trade/domain';
import { commonRoutes } from 'constants/web-app-constants';
import { filter, map } from 'rxjs';
import { CancelOpenOrderItemComponent } from '../cancel-open-order-item/cancel-open-order-item.component';
import { EditOrderComponent } from '../edit-order/edit-order.component';
import { OpenOrderItemComponent } from '../open-order-item/open-order-item.component';
import { OldOrderList } from './order-list-base.old';

@Component({
  selector: 'order-zero-open-orders',
  templateUrl: './open-orders.component.html',
  animations: [fadeInAnimation, expandTdDivsAnimation, onlyEnter],
  imports: [
    SorterLabelComponent,
    OpenOrderItemComponent,
    CancelOpenOrderItemComponent,
    TradingSessionComponent,
    EditOrderComponent,
    TranslocoPipe,
  ],
})
export class OpenOrdersComponent extends OldOrderList implements OnDestroy {
  @Input() public actionsEnabled = true;
  @Output() public openOrdersChanged = new EventEmitter<void>();

  public isin2quote = new Map<string, Quote>();
  public sorter: SortHelper;
  public isReady = false;
  public editOrder?: OrderData;
  public isSessionAuthorized = false;

  #confirmCancel: OrderData[] = [];
  readonly #quotesService = inject(PushQuoteService);
  readonly #cd = inject(ChangeDetectorRef);
  readonly #customerStateFacade = inject(CustomerStateFacade);
  readonly #router = inject(Router);
  readonly #tradeService = inject(TradeService);

  constructor() {
    super();
    this.sorter = new SortHelper('openDate', false, 'open-orders');
    this.sortAttribute = 'openDate';
    this.sortDirection = 'asc';
    this.pageSize = 1000; // show all open orders
  }

  public ngOnDestroy(): void {
    if (this.orders?.length) {
      const isins = this.orders.filter((order) => !!order?.isin).map((order) => order.isin);
      this.#quotesService.unsubscribeMulti([...new Set(isins)]);
    }
  }

  public sortBy(sortAttr: string): void {
    this.sorter.sortBy(this.orders, sortAttr);
    if (this.sortAttribute && this.sortAttribute === sortAttr) {
      // change direction
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else {
        this.sortDirection = 'asc';
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async loadOrders(_sorted: boolean): Promise<void> {
    const orders: OrderDataHolder = await this.dataService.loadOrdersHttp(OrderStatus.Open, this.pageSize);
    this.mergeLoadedOrders(orders.list);
    this.sorter.sortValues(this.orders);
    this.#updateQuotesSubscription();
    this.subscribeToTslUpdates();
    this.isReady = true;
  }

  public subscribeToTslUpdates = (): void => {
    this.dataService.msgSubject
      .pipe(
        filter((msg) => msg?.type === PushMessageType.tslupdate && msg.msgData),
        map(({ msgData }) => msgData as TSLUpdate),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ orderId, latestTslStop }) => this.#replaceStopPrice(orderId, latestTslStop));
  };

  #replaceStopPrice = (orderId: string, latestTslStop: number): void => {
    this.orders = this.orders.map((order) => {
      if (order.orderId === orderId) {
        order.latestTslStop = latestTslStop;
      }
      return order;
    });
  };

  public cancelOrder(order: OrderData): void {
    this.editOrder = undefined;
    this.#confirmCancel = [...this.#confirmCancel, order];
  }

  public toggleEditOrder(order: OrderData): void {
    this.#finishCancelling(order.orderId);
    this.editOrder = order.orderId === this.editOrder?.orderId ? undefined : order;
    if (this.editOrder) {
      this.#tradeService.loadHolidays();
      this.#customerStateFacade.loadCustomerAccountData(true);
    }
  }

  public askForCancellation(order: OrderData): boolean {
    return this.#confirmCancel.includes(order);
  }

  public cancelationResult(order: OrderData, orderCanceled: boolean): void {
    this.#finishCancelling(order.orderId);
    if (orderCanceled) {
      this.loadOrders(false);
      this.openOrdersChanged.emit();
    }
  }

  public redirectToOrdermask = (order: OrderData): Promise<boolean> =>
    this.#router.navigate([commonRoutes.postlogin.buyAndSell], {
      queryParams: { ...this.#tradeService.createQueryParams(order, true), editOrder: true },
    });

  #finishCancelling = (orderId: string): void => {
    this.#confirmCancel = this.#confirmCancel.filter((order) => orderId !== order.orderId);
  };

  #updateQuotesSubscription(): void {
    const isins = this.orders.map((ord) => ord.isin);
    this.#quotesService
      .subscribeMultiBuffered(isins, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((quotesMap) => {
        if (!quotesMap || Object.keys(quotesMap).length < 1) {
          return;
        }
        const tmpMap = new Map<string, Quote>();
        Object.keys(quotesMap).forEach((isin) => {
          tmpMap.set(isin, quotesMap[isin]);
        });
        this.isin2quote = new Map(tmpMap);
        // calling detectChanges only checks the current component and not the whole component tree
        this.#cd.detectChanges();
      });
  }
}
